// Which AI engine the platform runs on: Amazon Bedrock, or a direct key.
import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card, Badge } from './_shared';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BrainCircuit, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const PROFILES = [
  { id: 'global.anthropic.claude-opus-5', label: 'Claude Opus 5 — global routing' },
  { id: 'in.anthropic.claude-opus-5', label: 'Claude Opus 5 — India only' },
];

export default function AdminAiEngine() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get('/platform/ai-engine').then(r => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    setResult(null);
    try {
      const { data } = await api.put('/platform/ai-engine', {
        provider: patch.provider ?? cfg.provider,
        bedrockRegion: patch.bedrock_region ?? cfg.bedrock_region,
        bedrockModel: patch.bedrock_model ?? cfg.bedrock_model,
      });
      setCfg(c => ({ ...c, ...data }));
      toast.success('AI engine updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not switch the AI engine');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post('/platform/ai-engine/test');
      setResult(data);
      if (data.ok) toast.success(`Answered in ${data.seconds}s`);
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || 'The test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (!cfg) {
    return (
      <AdminLayout title="AI Engine" subtitle="Admin · Bedrock or direct key">
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="AI Engine" subtitle="Admin · Bedrock or direct key">
      <p className="text-sm text-slate-500 mb-6 max-w-3xl">
        Everything the platform analyses — contracts, claims, delay assessments, narratives —
        runs through one engine. On AWS it can run on Amazon Bedrock inside your own account,
        billed on your AWS invoice and signed with the container's own role, so no AWS keys
        are stored here.
      </p>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Card className="p-5 space-y-4" data-testid="ai-engine-card">
          <div className="flex items-center gap-2">
            <BrainCircuit className="w-4 h-4 text-sky-500" />
            <span className="text-sm font-semibold text-slate-800">Current engine</span>
            <Badge className="ml-auto" data-testid="ai-engine-badge">
              {cfg.provider === 'bedrock' ? 'Amazon Bedrock' : 'Direct key'}
            </Badge>
          </div>

          <div>
            <Label className="text-xs">Run the AI engine on</Label>
            <Select value={cfg.provider} disabled={saving}
                    onValueChange={v => save({ provider: v })}>
              <SelectTrigger className="mt-1" data-testid="ai-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bedrock">Amazon Bedrock (this AWS account)</SelectItem>
                <SelectItem value="anthropic">Direct Anthropic key</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {cfg.provider === 'bedrock' && (
            <div>
              <Label className="text-xs">Model routing</Label>
              <Select value={cfg.bedrock_model} disabled={saving}
                      onValueChange={v => save({ bedrock_model: v })}>
                <SelectTrigger className="mt-1" data-testid="ai-bedrock-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROFILES.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Region {cfg.bedrock_region}. India-only routing keeps inference inside the
                country; structured answers fall back to validated JSON there.
              </p>
            </div>
          )}

          {cfg.provider === 'anthropic' && (
            <p className="text-[11px] text-slate-500">
              Model {cfg.direct_model}.{' '}
              {cfg.directKeyPresent ? 'A key is configured on this deployment.'
                                    : 'No key is configured on this deployment.'}
            </p>
          )}

          <Button onClick={test} disabled={testing} className="w-full"
                  data-testid="ai-engine-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Test the engine
          </Button>

          {result && (
            <div className={`rounded-lg border p-3 text-[12px] ${result.ok
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'}`}
                 data-testid="ai-engine-test-result">
              <div className="flex items-center gap-2 font-medium">
                {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {result.ok ? `Answered in ${result.seconds}s` : 'The engine did not answer'}
              </div>
              <p className="mt-1 break-words">
                {result.ok ? `${result.model} replied "${result.reply}"`
                           : result.error}
              </p>
              {result.hint && <p className="mt-1">{result.hint}</p>}
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="ai-engine-notes">
          <h2 className="text-sm font-semibold text-slate-800 mb-2">Before switching to Bedrock</h2>
          <ul className="space-y-1.5 text-[12px] text-slate-600">
            <li>· The platform must be running on AWS — Bedrock is signed with the container's
              IAM role, which only exists there.
              {cfg.runningOnAws
                ? ' This deployment is on AWS.'
                : ' This deployment is not on AWS, so the test will fail here.'}</li>
            <li>· Enable access to Claude Opus 5 in the Bedrock console for that AWS account.</li>
            <li>· The deployment template grants the role the three permissions AWS requires
              for global inference — the profile, the regional model and the global model.</li>
            <li>· AI usage then appears on your AWS bill instead of Anthropic's.</li>
          </ul>
          <p className="text-[11px] text-slate-400 mt-3">{cfg.note}</p>
        </Card>
      </div>
    </AdminLayout>
  );
}
